import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReadonlyQuat, ReadonlyVec3, ReadonlyVec4 } from "gl-matrix";

import { type RootState } from "app";
import { selectBookmark, setViewpoint } from "features/render";
import { PointSnapInfo } from "features/render/measureSnap";
import { VecRGB } from "utils/color";

export type LaserDirection = "up" | "down" | "left" | "right" | "zUp" | "zDown";

export interface OutlineGroup {
    name: string;
    color: VecRGB;
    hidden: boolean;
    ids: number[];
}

export interface TraceMeasurement {
    start?: ReadonlyVec3;
    end?: ReadonlyVec3;
    startIdx?: number;
    endIdx?: number;
}

export type LaserPoint = {
    position: ReadonlyVec3;
    normal?: ReadonlyVec3;
    objectId?: number;
    pathIdx?: number;
    instanceIdx?: number;
    connection?: PointSnapInfo;
};

export interface OutlineLaser {
    id: string;
    laserPosition: ReadonlyVec3;
    laserObjectId?: number;
    xDir?: ReadonlyVec3;
    zDir?: ReadonlyVec3;
    left: LaserPoint[];
    right: LaserPoint[];
    down: LaserPoint[];
    up: LaserPoint[];
    zDown: LaserPoint[];
    zUp: LaserPoint[];
    hasMoreIntersections: Record<LaserDirection, boolean>;
    measurementX?: TraceMeasurement;
    measurementY?: TraceMeasurement;
    measurementZ?: TraceMeasurement;
    laserPlanes?: ReadonlyVec4[];
    attachedToPlane?: boolean;
}

export interface LaserLockedRotation {
    normal: ReadonlyVec3;
    rotation: ReadonlyQuat;
    pos: ReadonlyVec3;
    intersection: {
        up?: ReadonlyVec3;
        down?: ReadonlyVec3;
        left?: ReadonlyVec3;
        right?: ReadonlyVec3;
    };
}

export function getMeasurePointsFromTracer(measurement: TraceMeasurement, startAr: LaserPoint[], endAr: LaserPoint[]) {
    if (measurement.start && measurement.end) {
        return [{ position: measurement.start }, { position: measurement.end }];
    }
    if (
        measurement.startIdx !== undefined &&
        measurement.startIdx < startAr.length &&
        measurement.endIdx !== undefined &&
        measurement.endIdx < endAr.length
    ) {
        return [startAr[measurement.startIdx], endAr[measurement.endIdx]];
    }
    return undefined;
}

const initialState = {
    outlineGroups: [] as OutlineGroup[],
    laser3d: true,
    lasers: [] as OutlineLaser[],
    laserPlane: undefined as
        | {
              normalOffset: ReadonlyVec4;
              rotation: number;
          }
        | undefined,
    lockRotation: true,
    lockedRotation: null as null | LaserLockedRotation,
    followClippingPlane: true,
    draggingLaser: null as null | OutlineLaser,
    orthoAxisAligned: false,
    hideGizmo: false,
};

type State = typeof initialState;

const EMPTY_OUTLINE_GROUPS: OutlineGroup[] = [];

export const outlineLaserSlice = createSlice({
    name: "outlineLaser",
    initialState: initialState,
    reducers: {
        setOutlineGroups: (state, action: PayloadAction<State["outlineGroups"]>) => {
            state.outlineGroups = action.payload.length === 0 ? EMPTY_OUTLINE_GROUPS : action.payload;
        },
        toggleHideOutlineGroup: (state, action: PayloadAction<{ name: OutlineGroup["name"]; hide?: boolean }>) => {
            state.outlineGroups = state.outlineGroups.map((group) =>
                group.name === action.payload.name
                    ? { ...group, hidden: action.payload.hide === undefined ? !group.hidden : action.payload.hide }
                    : group,
            );
        },
        setLaser3d: (state, action: PayloadAction<State["laser3d"]>) => {
            state.laser3d = action.payload;
        },
        setLaserPlane: (state, action: PayloadAction<State["laserPlane"]>) => {
            state.laserPlane = action.payload;
        },
        addLaser: (state, action: PayloadAction<OutlineLaser>) => {
            state.lasers = state.lasers.concat([action.payload]);
        },
        replaceLaser: (state, action: PayloadAction<{ laser: OutlineLaser; replaceId: string }>) => {
            const idx = state.lasers.findIndex((l) => l.id === action.payload.replaceId);
            if (idx === -1) {
                return;
            }
            state.lasers[idx] = action.payload.laser;
        },
        setLasers: (state, action: PayloadAction<State["lasers"]>) => {
            state.lasers = action.payload;
        },
        setLaser: (state, action: PayloadAction<{ idx: number; trace: OutlineLaser }>) => {
            state.lasers[action.payload.idx] = action.payload.trace;
        },
        clear: (state) => {
            state.lasers = [];
        },
        setMeasurementX: (state, action: PayloadAction<{ laserId: string; measurement: TraceMeasurement }>) => {
            const laser = state.lasers.find((l) => l.id === action.payload.laserId);
            if (laser) {
                laser.measurementX = action.payload.measurement;
            }
        },
        setMeasurementY: (state, action: PayloadAction<{ laserId: string; measurement: TraceMeasurement }>) => {
            const laser = state.lasers.find((l) => l.id === action.payload.laserId);
            if (laser) {
                laser.measurementY = action.payload.measurement;
            }
        },
        setMeasurementZ: (state, action: PayloadAction<{ laserId: string; measurement: TraceMeasurement }>) => {
            const laser = state.lasers.find((l) => l.id === action.payload.laserId);
            if (laser) {
                laser.measurementZ = action.payload.measurement;
            }
        },
        removeMeasurementX: (state, action: PayloadAction<string>) => {
            const laser = state.lasers.find((l) => l.id === action.payload);
            if (!laser) {
                return;
            }
            delete laser.measurementX;
            removeLaserIfNoMeasurements(state, laser);
        },
        removeMeasurementY: (state, action: PayloadAction<string>) => {
            const laser = state.lasers.find((l) => l.id === action.payload);
            if (!laser) {
                return;
            }
            delete laser.measurementY;
            removeLaserIfNoMeasurements(state, laser);
        },
        removeMeasurementZ: (state, action: PayloadAction<string>) => {
            const laser = state.lasers.find((l) => l.id === action.payload);
            if (!laser) {
                return;
            }
            delete laser.measurementZ;
            removeLaserIfNoMeasurements(state, laser);
        },
        removeLaser: (state, action: PayloadAction<string>) => {
            state.lasers = state.lasers.filter((l) => l.id !== action.payload);
        },
        setLockRotation: (state, action: PayloadAction<State["lockRotation"]>) => {
            state.lockRotation = action.payload;
        },
        setLockedRotation: (state, action: PayloadAction<State["lockedRotation"]>) => {
            state.lockedRotation = action.payload;
        },
        setFollowClippingPlane: (state, action: PayloadAction<State["followClippingPlane"]>) => {
            state.followClippingPlane = action.payload;
        },
        setDraggingLaser: (state, action: PayloadAction<State["draggingLaser"]>) => {
            state.draggingLaser = action.payload;
        },
        setHideGizmo: (state, action: PayloadAction<State["hideGizmo"]>) => {
            state.hideGizmo = action.payload;
        },
        setOrthoAxisAligned: (state, action: PayloadAction<State["orthoAxisAligned"]>) => {
            state.orthoAxisAligned = action.payload;
        },
    },
    extraReducers(builder) {
        builder.addCase(selectBookmark, (state, action) => {
            const { outlineMeasure } = action.payload;

            if (!outlineMeasure) {
                state.laserPlane = undefined;
                state.lasers = [];
                return;
            }

            state.laserPlane = outlineMeasure.laserPlane;
            state.lasers = outlineMeasure.lasers.map((t) => {
                let zDir = t.zDir;
                if (!zDir && t.laserPlanes && t.laserPlanes.length > 0) {
                    // If zDir is not stored, but laserPlanes are, we can compute it
                    const plane = t.laserPlanes[0];
                    zDir = [plane[0], plane[1], plane[2]];
                }

                const isLegacy3dLaser = Boolean(t.laserPlanes?.length);
                const attachedToPlane = t.attachedToPlane ?? !isLegacy3dLaser;

                return {
                    id: crypto.randomUUID(),
                    laserPosition: t.laserPosition,
                    xDir: t.xDir,
                    zDir,
                    left: [],
                    right: [],
                    down: [],
                    up: [],
                    zDown: [],
                    zUp: [],
                    hasMoreIntersections: {
                        left: true,
                        right: true,
                        down: true,
                        up: true,
                        zDown: true,
                        zUp: true,
                    },
                    measurementX: t.measurementX
                        ? {
                              start: t.measurementX.start,
                              end: t.measurementX.end,
                          }
                        : undefined,
                    measurementY: t.measurementY
                        ? {
                              start: t.measurementY.start,
                              end: t.measurementY.end,
                          }
                        : undefined,
                    measurementZ: t.measurementZ
                        ? {
                              start: t.measurementZ.start,
                              end: t.measurementZ.end,
                          }
                        : undefined,
                    laserPlanes: t.laserPlanes,
                    attachedToPlane,
                };
            });
        });
        builder.addCase(setViewpoint, (state, action) => {
            const { measurements } = action.payload;

            if (!measurements?.lasers) {
                if (action.meta.ignoreUndefined) {
                    return;
                }
                state.laserPlane = undefined;
                state.lasers = [];
                return;
            }

            state.lasers = measurements.lasers.map((t) => ({
                id: crypto.randomUUID(),
                laserPosition: t.laser_position,
                xDir: t.x_dir,
                zDir: t.z_dir,
                left: [],
                right: [],
                down: [],
                up: [],
                zDown: [],
                zUp: [],
                hasMoreIntersections: {
                    left: true,
                    right: true,
                    down: true,
                    up: true,
                    zDown: true,
                    zUp: true,
                },
                measurementX: t.measurement_x
                    ? {
                          start: t.measurement_x.start.position,
                          end: t.measurement_x.end.position,
                      }
                    : undefined,
                measurementY: t.measurement_y
                    ? {
                          start: t.measurement_y.start.position,
                          end: t.measurement_y.end.position,
                      }
                    : undefined,
                measurementZ: t.measurement_z
                    ? {
                          start: t.measurement_z.start.position,
                          end: t.measurement_z.end.position,
                      }
                    : undefined,
                attachedToPlane: t.attached_to_plane ?? true,
            }));
        });
    },
});

function removeLaserIfNoMeasurements(state: State, laser: OutlineLaser) {
    if (!laser.measurementX && !laser.measurementY && !laser.measurementZ) {
        state.lasers = state.lasers.filter((l) => l.id !== laser.id);
    }
}

export const selectOutlineGroups = (state: RootState) => state.clippingOutline.outlineGroups;
export const selectVisibleOutlineGroups = createSelector(selectOutlineGroups, (groups) =>
    groups.filter((group) => !group.hidden),
);
export const selectOutlineLaserPlane = (state: RootState) => state.clippingOutline.laserPlane;
export const selectOutlineLasers = (state: RootState) => state.clippingOutline.lasers;
export const selectOutlineLaser3d = (state: RootState) => state.clippingOutline.laser3d;
export const selectLockRotation = (state: RootState) => state.clippingOutline.lockRotation;
export const selectLockedRotation = (state: RootState) => state.clippingOutline.lockedRotation;
export const selectFollowClippingPlane = (state: RootState) => state.clippingOutline.followClippingPlane;
export const selectDraggingLaser = (state: RootState) => state.clippingOutline.draggingLaser;
export const selectHideGizmo = (state: RootState) => state.clippingOutline.hideGizmo;
export const selectOrthoAxisAligned = (state: RootState) => state.clippingOutline.orthoAxisAligned;

const { actions, reducer } = outlineLaserSlice;
export { actions as clippingOutlineLaserActions, reducer as clippingOutlineLaserReducer };
